import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Input, NgZone, OnChanges, Output, SimpleChanges, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { isEnumValue } from '@common/util/enum';
import { ChecklistTemplateItem } from '@portal-core/checklist-templates/models/checklist-template-item.model';
import { ChecklistTemplate } from '@portal-core/checklist-templates/models/checklist-template.model';
import { ChecklistTemplatesService } from '@portal-core/checklist-templates/services/checklist-templates.service';
import { ErrorService } from '@portal-core/errors/services/error.service';
import { ProjectChecklistItemType } from '@portal-core/project-checklists/enums/project-checklist-item-type.enum';
import { ProjectChecklistType } from '@portal-core/project-checklists/enums/project-checklist-type.enum';
import { ProjectChecklistItem } from '@portal-core/project-checklists/models/project-checklist-item.model';
import { ProjectChecklist } from '@portal-core/project-checklists/models/project-checklist.model';
import { ProjectChecklistsService } from '@portal-core/project-checklists/services/project-checklists/project-checklists.service';
import { ProjectFilesTreeComponent } from '@portal-core/project-files/components/project-files-tree/project-files-tree.component';
import { LoadingState } from '@portal-core/util/loading-state';
import { first, Observable } from 'rxjs';

interface RowItemForm {
  Id: FormControl<number | string>;
  Name: FormControl<string>;
  Note: FormControl<string>;
  Order: FormControl<number>;
  Type: FormControl<ProjectChecklistItemType>;
}

export interface ProjectChecklistRowsForm {
  addPaths: FormControl<string[]>;
  addRow: FormControl<string>;
  items: FormArray<FormGroup<RowItemForm>>;
}

export interface FormData {
  message: string
  object: ChecklistTemplate | ProjectChecklist,
}

@Component({
  selector: 'mc-project-checklist-rows-form',
  templateUrl: './project-checklist-rows-form.component.html',
  styleUrls: ['./project-checklist-rows-form.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProjectChecklistRowsFormComponent implements OnChanges {
  @Input() branchName?: string;
  @Input() checklist?: ProjectChecklist;
  @Input() licenseId: number;
  @Input() projectId: number;
  @Input() showFormFooter: boolean = true;
  @Input() template?: ChecklistTemplate;
  @Input() type: ProjectChecklistType;

  @Output() cancel: EventEmitter<void> = new EventEmitter<void>();
  @Output() saved: EventEmitter<void> = new EventEmitter<void>();

  @ViewChild('projectFileSelect', { static: false }) projectFileSelect: ProjectFilesTreeComponent;
  @ViewChild('rowList', { static: true }) checklistRowList: ElementRef<HTMLElement>;

  ProjectChecklistType: typeof ProjectChecklistType = ProjectChecklistType;

  checklistRowsForm: FormGroup<ProjectChecklistRowsForm>;
  pathFiltersChanged: boolean = false;
  savingState: LoadingState<string> = new LoadingState<string>();

  get items(): FormArray<FormGroup<RowItemForm>> {
    return this.checklistRowsForm?.get('items') as FormArray;
  }

  get dirty(): boolean {
    if (this.checklist?.Type == ProjectChecklistType.ProjectFiles) {
      return this.pathFiltersChanged;
    }

    if (this.checklist?.Type == ProjectChecklistType.GenericList || this.template?.Type == ProjectChecklistType.GenericList) {
      return this.checklistRowsForm?.controls.items.dirty;
    }
  }

  get checklistType(): ProjectChecklistType {
    if (isEnumValue(ProjectChecklistType, this.type)) {
      return this.type;
    } else {
      return this.checklist?.Type ?? this.template?.Type;
    }
  }

  constructor(
    private fb: FormBuilder,
    private errorService: ErrorService,
    private projectChecklistsService: ProjectChecklistsService,
    private checklistTemplatesService: ChecklistTemplatesService,
    private snackBar: MatSnackBar,
    private ngZone: NgZone
  ) {
    this.buildForm();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.checklist || changes.template || changes.type) {
      this.resetForm();
    }
  }

  // Rows for non ProjectFile type checklists.
  onAddItem() {
    const addRowControl = this.checklistRowsForm.get('addRow');

    if (addRowControl?.value) {
      this.items.push(this.createRowItemFormGroup({
        Id: 0,
        Type: ProjectChecklistItemType.File,
        Name: addRowControl.value,
        Order: this.items.length,
        Note: ''
      }));

      this.items.markAsDirty();
      addRowControl.setValue('');

      this.ngZone.onStable.asObservable().pipe(first()).subscribe(() => {
        this.checklistRowList.nativeElement.scrollTop = this.checklistRowList.nativeElement.scrollHeight;
      });
    }
  }

  onCancelClicked() {
    this.cancel.emit();
  }

  onMoveItem(item: ChecklistTemplateItem | ProjectChecklistItem, moveUp: boolean) {
    const prevIndex = this.items.controls.findIndex(c => c.value === item);
    const moveUpIndex = prevIndex - 1 <= 0 ? 0 : prevIndex - 1;
    const moveDownIndex = (prevIndex + 1 >= this.items.length ? prevIndex : prevIndex + 1);
    const newIndex = moveUp ? moveUpIndex : moveDownIndex;

    // return as to not dirty the control
    if ((moveUp && prevIndex == 0) || (!moveUp && prevIndex == this.items.length - 1)) {
      return;
    }

    this.onRemoveItem(item);
    this.items.insert(newIndex, this.createRowItemFormGroup(item));

    // reset list order
    this.items.controls.forEach((control, index) => {
      control.value['Order'] = index;
    });

    this.items.patchValue(this.items.value);
    this.items.markAsDirty();
  }

  onRemoveItem(item: ChecklistTemplateItem | ProjectChecklistItem) {
    const index = this.items.controls.findIndex(c => c.value === item);
    this.items.removeAt(index);
    this.items.markAsDirty();
  }

  onPathFilterChanged(pathFilters: string[]) {
    this.checklistRowsForm.get('addPaths')?.setValue([pathFilters.join(';')]);

    if (this.checklist?.Filters != pathFilters?.toString()) {
      this.pathFiltersChanged = true;
    }
    else {
      this.pathFiltersChanged = false;
    }
  }

  onSubmit() {
    if (this.checklist) {
      this.checklistFormSubmit((checklist) => this.projectChecklistsService.updateProjectChecklist$(checklist));
    } else if (this.template) {
      this.templateFormSubmit((template) => this.checklistTemplatesService.updateChecklistTemplate$(template));
    }
  }

  public checkViewportSize() {
    this.projectFileSelect?.checkViewportSize();
  }

  public getItems<T = ChecklistTemplateItem | ProjectChecklistItem>(): T[] {
    return this.items.value as T[];
  }

  private checklistFormSubmit(func: (checklist: ProjectChecklist) => Observable<any>) {
    const checklist: ProjectChecklist = {
      ...this.checklist,
      Filters: this.checklistRowsForm?.get('addPaths').value?.join(';'),
      Items: this.getItems()
    };

    const dataObj: FormData = {
      object: checklist,
      message: 'checklist'
    };

    this.formSubmit(func, dataObj);
  }

  private templateFormSubmit(func: (template: ChecklistTemplate) => Observable<any>) {
    const template: ChecklistTemplate = {
      ...this.template,
      Items: this.getItems()
    };

    const dataObj: FormData = {
      object: template,
      message: 'checklist template'
    };

    this.formSubmit(func, dataObj);
  }

  private formSubmit(func: (obj: ChecklistTemplate | ProjectChecklist) => Observable<any>, formData: FormData) {
    if (!this.checklistRowsForm.valid) {
      return;
    }

    this.savingState.update(true);

    func(formData.object).subscribe(() => {
      this.savingState.update(false);
      this.checklistRowsForm.markAsPristine();
      this.pathFiltersChanged = false;
      this.snackBar.open('The ' + formData.message + ' has been updated.', 'OK', { duration: 2500 });
      this.saved.emit();
    }, error => {
      this.savingState.update(false, `Unable to update the ${formData.message}`, this.errorService.getErrorMessages(error));
    });
  }

  private buildForm() {
    this.checklistRowsForm = this.fb.group({
      addPaths: new FormControl<string[]>({ value: null, disabled: this.checklist ? this.checklist.Type !== ProjectChecklistType.ProjectFiles : false }, [Validators.required]),
      addRow: new FormControl<string>({ value: null, disabled: this.checklist ? this.checklist.Type === ProjectChecklistType.ProjectFiles : false }),
      items: new FormArray([])
    });
  }

  private resetForm() {
    this.checklistRowsForm.reset({
      addPaths: this.checklist?.Filters?.split(';') ?? ['']
    });

    if (this.checklist?.Items) {
      this.setItems(this.checklist?.Items);
    }

    if (this.template?.Items) {
      this.setItems(this.template?.Items);
    }

    // Enable/disable fields based on the type of checklist
    // The type input overrides the checklist/template type if set
    if (this.type === ProjectChecklistType.GenericList) {
      this.checklistRowsForm.get('addPaths').disable();
    } else if (this.type === ProjectChecklistType.ProjectFiles) {
      this.checklistRowsForm.get('addPaths').enable();
    } else if (this.checklist?.Type === ProjectChecklistType.GenericList || this.template?.Type === ProjectChecklistType.GenericList) {
      this.checklistRowsForm.get('addPaths').disable();
    } else if (this.checklist?.Type === ProjectChecklistType.ProjectFiles || this.template?.Type === ProjectChecklistType.ProjectFiles) {
      this.checklistRowsForm.get('addPaths').enable();
    }
  }

  private setItems(items: ChecklistTemplateItem[] | ProjectChecklistItem[]) {
    this.items.clear();

    items?.sort((itemA, itemB) => {
      return itemA.Order - itemB.Order;
    })?.forEach(item => {
      this.items.push(this.createRowItemFormGroup(item));
    });
  }

  private createRowItemFormGroup(item: ChecklistTemplateItem | ProjectChecklistItem): FormGroup<RowItemForm> {
    return this.fb.group<RowItemForm>({
      Id: new FormControl<number | string>(item.Id),
      Name: new FormControl<string>(item.Name, [Validators.required, Validators.maxLength(250)]),
      Note: new FormControl<string>(item.Note),
      Order: new FormControl<number>(item.Order),
      Type: new FormControl<ProjectChecklistItemType>(item.Type)
    });
  }
}
